import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardActions,
  Button, Chip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select,
  FormControl, InputLabel, LinearProgress, List, ListItem, ListItemIcon, ListItemText,
  IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import { nodeNetworkService } from '../services/nodeNetworkService';
import ofpService from '../services/ofpService';

const NETWORK_TYPE_LABELS = {
  energy_corridor: 'Energy Corridor',
  supply_chain: 'Supply Chain',
  circular_economy: 'Circular Economy',
  other: 'Other'
};

const NETWORK_TYPE_COLORS = {
  energy_corridor: 'warning',
  supply_chain: 'primary',
  circular_economy: 'success',
  other: 'default'
};

const NODE_TYPE_OPTIONS = [
  { value: 'extraction_raw_material',  label: 'Extraction Raw Material' },
  { value: 'agricultural_production',  label: 'Agricultural Production' },
  { value: 'refinery_intermediate',    label: 'Refinery Intermediate Processing' },
  { value: 'component_manufacturing',  label: 'Component Manufacturing' },
  { value: 'final_assembly',           label: 'Final Assembly' },
  { value: 'warehouse_distribution',   label: 'Warehouse Distribution' },
  { value: 'transit_hub',              label: 'Transit Hub' },
  { value: 'retail_point_of_sale',     label: 'Retail Point of Sale' },
  { value: 'use_phase',                label: 'Use Phase' },
  { value: 'end_of_life_recovery',     label: 'End of Life Recovery' },
  { value: 'corporate_admin',          label: 'Corporate Admin' },
];
const NODE_ROLE_OPTIONS = [
  { value: 'producer',    label: 'Producer' },
  { value: 'processor',   label: 'Processor' },
  { value: 'trader',      label: 'Trader' },
  { value: 'transporter', label: 'Transporter' },
  { value: 'storage',     label: 'Storage' },
  { value: 'consumer',    label: 'Consumer' },
  { value: 'regulator',   label: 'Regulator' },
  { value: 'auditor',     label: 'Auditor' },
  { value: 'other',       label: 'Other' },
];

function NodeNetworkPage() {
  const [networks, setNetworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create network dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [form, setForm] = useState({ network_name: '', network_description: '', network_type: 'supply_chain' });

  // Edit network dialog
  const [editDialog, setEditDialog] = useState({ open: false, network: null });
  const [editForm, setEditForm] = useState({ network_name: '', network_description: '', network_type: 'supply_chain', status: 'active' });
  const [editSaving, setEditSaving] = useState(false);

  // Add node dialog
  const [addNodeDialog, setAddNodeDialog] = useState({ open: false, networkId: null });
  const [nodeForm, setNodeForm] = useState({ node_name: '', node_type: 'extraction_raw_material', node_role: 'producer', ofp_organization_id: '', ofp_facility_id: '' });
  const [nodeSaving, setNodeSaving] = useState(false);
  const [ofpOrganizations, setOfpOrganizations] = useState([]);
  const [ofpFacilities, setOfpFacilities] = useState([]);
  const [ofpOrgsLoading, setOfpOrgsLoading] = useState(false);

  // PACT sync dialog
  const [pactSyncOpen, setPactSyncOpen] = useState(false);
  const [pactSyncNetwork, setPactSyncNetwork] = useState(null);
  const [pactSyncResults, setPactSyncResults] = useState([]);
  const [pactSyncDone, setPactSyncDone] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadNetworks();
  }, []);

  // Load OFP organizations when Add Node dialog opens
  useEffect(() => {
    if (!addNodeDialog.open) return;
    setOfpOrgsLoading(true);
    ofpService.getOrganizations()
      .then(data => setOfpOrganizations(data ?? []))
      .catch(() => setOfpOrganizations([]))
      .finally(() => setOfpOrgsLoading(false));
  }, [addNodeDialog.open]);

  // Load facilities filtered by selected organization; clear facility when org changes
  useEffect(() => {
    if (!addNodeDialog.open) return;
    const orgId = nodeForm.ofp_organization_id || null;
    if (!orgId) { setOfpFacilities([]); return; }
    ofpService.getFacilities(orgId)
      .then(data => setOfpFacilities(data ?? []))
      .catch(() => setOfpFacilities([]));
  }, [nodeForm.ofp_organization_id, addNodeDialog.open]);

  const loadNetworks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await nodeNetworkService.getNetworks();
      setNetworks(data ?? []);
    } catch (err) {
      setError('Failed to load networks: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.network_name.trim()) return;
    try {
      await nodeNetworkService.createNetwork(form);
      setCreateDialogOpen(false);
      setForm({ network_name: '', network_description: '', network_type: 'supply_chain' });
      await loadNetworks();
    } catch (err) {
      setError('Failed to create network: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEditOpen = (network) => {
    setEditForm({
      network_name: network.network_name,
      network_description: network.network_description || '',
      network_type: network.network_type || 'supply_chain',
      status: network.status || 'active'
    });
    setEditDialog({ open: true, network });
  };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      await nodeNetworkService.updateNetwork(editDialog.network.network_id, editForm);
      setEditDialog({ open: false, network: null });
      await loadNetworks();
    } catch (err) {
      setError('Failed to update network: ' + (err.response?.data?.error || err.message));
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddNodeOpen = (networkId) => {
    setNodeForm({ node_name: '', node_type: 'extraction_raw_material', node_role: 'producer', ofp_organization_id: '', ofp_facility_id: '' });
    setOfpFacilities([]);
    setAddNodeDialog({ open: true, networkId });
  };

  const handleAddNodeSave = async () => {
    if (!nodeForm.node_name.trim()) return;
    setNodeSaving(true);
    try {
      const payload = {
        node_name: nodeForm.node_name,
        node_type: nodeForm.node_type,
        node_role: nodeForm.node_role
      };
      if (nodeForm.ofp_organization_id) payload.ofp_organization_id = nodeForm.ofp_organization_id;
      if (nodeForm.ofp_facility_id) payload.ofp_facility_id = nodeForm.ofp_facility_id;
      await nodeNetworkService.createNode(addNodeDialog.networkId, payload);
      setAddNodeDialog({ open: false, networkId: null });
      await loadNetworks();
    } catch (err) {
      setError('Failed to add node: ' + (err.response?.data?.error || err.message));
    } finally {
      setNodeSaving(false);
    }
  };

  const handleDelete = async (networkId) => {
    if (!window.confirm('Delete this network and all its nodes?')) return;
    try {
      await nodeNetworkService.deleteNetwork(networkId);
      await loadNetworks();
    } catch (err) {
      setError('Failed to delete network: ' + (err.response?.data?.error || err.message));
    }
  };

  const handlePushNetworkToPACT = async (network) => {
    try {
      setPactSyncOpen(true);
      setPactSyncNetwork(network);
      setPactSyncResults([]);
      setPactSyncDone(false);
      const result = await nodeNetworkService.pushNetworkToPACT(network.network_id);
      const items = [];
      (result.nodes?.pushed || []).forEach(n => items.push({ type: 'success', label: `Node: ${n.name || n.pact_node_id}`, detail: 'Pushed to PACT' }));
      (result.nodes?.skipped || []).forEach(n => items.push({ type: 'skip', label: `Node: ${n.name || n.pact_node_id}`, detail: 'Already synced' }));
      (result.nodes?.errors || []).forEach(n => items.push({ type: 'error', label: `Node: ${n.name || 'unknown'}`, detail: n.error }));
      (result.connections?.pushed || []).forEach(c => items.push({ type: 'success', label: `Connection: ${c.from_node_id} → ${c.to_node_id}`, detail: 'Invitation created' }));
      (result.connections?.skipped || []).forEach(c => items.push({ type: 'skip', label: `Connection: ${c.from_node_id || 'unknown'} → ${c.to_node_id || 'unknown'}`, detail: 'Already synced or pending' }));
      (result.connections?.errors || []).forEach(c => items.push({ type: 'error', label: `Connection: ${c.from_node_id || 'unknown'} → ${c.to_node_id || 'unknown'}`, detail: c.error }));
      setPactSyncResults(items);
      setPactSyncDone(true);
      await loadNetworks();
    } catch (err) {
      setPactSyncResults([{ type: 'error', label: 'Push failed', detail: err.response?.data?.error || err.message }]);
      setPactSyncDone(true);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <DeviceHubIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">Node Network</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Network
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {networks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DeviceHubIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">
            No networks yet. Create one to get started.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {networks.map(network => (
            <Grid item xs={12} sm={6} md={4} key={network.network_id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" noWrap sx={{ maxWidth: 160 }}>
                      {network.network_name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        label={network.status}
                        size="small"
                        color={network.status === 'active' ? 'success' : 'default'}
                      />
                      <Tooltip title="Edit network">
                        <IconButton size="small" onClick={() => handleEditOpen(network)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Chip
                    label={NETWORK_TYPE_LABELS[network.network_type] || network.network_type}
                    size="small"
                    color={NETWORK_TYPE_COLORS[network.network_type] || 'default'}
                    sx={{ mb: 1 }}
                  />
                  {network.network_description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {network.network_description.length > 100
                        ? network.network_description.slice(0, 100) + '…'
                        : network.network_description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {network.node_count} node{network.node_count !== 1 ? 's' : ''}
                  </Typography>
                  {network.origin_node && network.destination_node && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {network.origin_node.node_name} → {network.destination_node.node_name}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate(`/node-network/${network.network_id}/graph`)}
                  >
                    View Graph
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonAddIcon />}
                    onClick={() => handleAddNodeOpen(network.network_id)}
                  >
                    Add Node
                  </Button>
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => handlePushNetworkToPACT(network)}
                    variant="outlined"
                  >
                    Sync to PACT
                  </Button>
                  <Button size="small" onClick={() => handleDelete(network.network_id)} color="error">
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Network Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Network</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Network Name"
              value={form.network_name}
              onChange={e => setForm(f => ({ ...f, network_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={form.network_description}
              onChange={e => setForm(f => ({ ...f, network_description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Network Type</InputLabel>
              <Select
                value={form.network_type}
                label="Network Type"
                onChange={e => setForm(f => ({ ...f, network_type: e.target.value }))}
              >
                {Object.entries(NETWORK_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.network_name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Network Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, network: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Network</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Network Name"
              value={editForm.network_name}
              onChange={e => setEditForm(f => ({ ...f, network_name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={editForm.network_description}
              onChange={e => setEditForm(f => ({ ...f, network_description: e.target.value }))}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Network Type</InputLabel>
              <Select
                value={editForm.network_type}
                label="Network Type"
                onChange={e => setEditForm(f => ({ ...f, network_type: e.target.value }))}
              >
                {Object.entries(NETWORK_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editForm.status}
                label="Status"
                onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, network: null })} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving || !editForm.network_name.trim()}>
            {editSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Node Dialog */}
      <Dialog open={addNodeDialog.open} onClose={() => setAddNodeDialog({ open: false, networkId: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Add Node</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Node Name"
              value={nodeForm.node_name}
              onChange={e => setNodeForm(f => ({ ...f, node_name: e.target.value }))}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Node Type</InputLabel>
              <Select
                value={nodeForm.node_type}
                label="Node Type"
                onChange={e => setNodeForm(f => ({ ...f, node_type: e.target.value }))}
              >
                {NODE_TYPE_OPTIONS.map(t => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Node Role</InputLabel>
              <Select
                value={nodeForm.node_role}
                label="Node Role"
                onChange={e => setNodeForm(f => ({ ...f, node_role: e.target.value }))}
              >
                {NODE_ROLE_OPTIONS.map(r => (
                  <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>OFP Organization</InputLabel>
              <Select
                value={nodeForm.ofp_organization_id}
                label="OFP Organization"
                onChange={e => setNodeForm(f => ({ ...f, ofp_organization_id: e.target.value, ofp_facility_id: '' }))}
                disabled={ofpOrgsLoading}
              >
                {ofpOrgsLoading
                  ? <MenuItem value=""><em>Loading...</em></MenuItem>
                  : ofpOrganizations.map(o => (
                      <MenuItem key={o.organization_id} value={o.organization_id}>{o.name}</MenuItem>
                    ))
                }
              </Select>
            </FormControl>
            <FormControl fullWidth disabled={!nodeForm.ofp_organization_id}>
              <InputLabel>OFP Facility (optional)</InputLabel>
              <Select
                value={nodeForm.ofp_facility_id}
                label="OFP Facility (optional)"
                onChange={e => setNodeForm(f => ({ ...f, ofp_facility_id: e.target.value }))}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {ofpFacilities.map(fac => (
                  <MenuItem key={fac.facility_id} value={fac.facility_id}>{fac.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddNodeDialog({ open: false, networkId: null })} disabled={nodeSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddNodeSave} disabled={nodeSaving || !nodeForm.node_name.trim() || !nodeForm.ofp_organization_id}>
            {nodeSaving ? <CircularProgress size={20} /> : 'Add Node'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PACT Sync Progress Dialog */}
      <Dialog open={pactSyncOpen} onClose={pactSyncDone ? () => setPactSyncOpen(false) : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>Sync to PACT Directory</DialogTitle>
        <DialogContent>
          {pactSyncNetwork && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pushing <strong>{pactSyncNetwork.network_name}</strong> to PACT Directory...
            </Typography>
          )}
          {!pactSyncDone && <LinearProgress sx={{ mb: 2 }} />}
          {pactSyncResults.length > 0 && (
            <List dense>
              {pactSyncResults.map((item, idx) => (
                <ListItem key={idx} disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {item.type === 'success' && <CheckCircleIcon fontSize="small" color="success" />}
                    {item.type === 'error' && <ErrorIcon fontSize="small" color="error" />}
                    {item.type === 'skip' && <SkipNextIcon fontSize="small" color="disabled" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    secondary={item.detail}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        {pactSyncDone && (
          <DialogActions>
            <Button onClick={() => setPactSyncOpen(false)}>Close</Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
}

export default NodeNetworkPage;

